import React, { useState, useMemo } from 'react';
import { Calendar, Trophy, Users, Trash2, Copy, Loader2, Search, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface SavedLineup {
  id: string;
  data_escalacao: string;
  tipo_formacao: string;
  formacao_nome: string;
  jogadores_titulares: any[];
  jogadores_reservas: string[];
  criado_em: string;
}

interface SavedLineupsProps {
  lineups: SavedLineup[];
  onLoadLineup: (lineup: SavedLineup) => void;
  onDeleteLineup: (lineupId: string) => void;
  onDuplicateLineup: (lineup: SavedLineup) => void;
  loading?: boolean;
  deleting?: string | null;
}

const SavedLineups: React.FC<SavedLineupsProps> = ({
  lineups,
  onLoadLineup,
  onDeleteLineup,
  onDuplicateLineup,
  loading = false,
  deleting = null
}) => {
  const [filterType, setFilterType] = useState<'all' | 'month' | 'specific'>('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10)); // YYYY-MM-DD
  const [searchTerm, setSearchTerm] = useState('');

  const getFormationTypeColor = (type: string) => {
    switch (type) {
      case 'ofensiva':
        return 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400';
      case 'defensiva':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400';
      case 'neutra':
        return 'bg-gray-100 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400';
    }
  };

  const getFormationTypeIcon = (type: string) => {
    switch (type) {
      case 'ofensiva':
        return '⚔️';
      case 'defensiva':
        return '🛡️';
      case 'neutra':
        return '⚖️';
      default:
        return '📋';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      // Para strings no formato "YYYY-MM-DD HH:MM:SS", extrair apenas a parte da data
      const dateOnly = dateString.split(' ')[0]; // Pega apenas "YYYY-MM-DD"
      const [year, month, day] = dateOnly.split('-').map(Number);
      // Criar data usando fuso horário local (sem conversão UTC)
      const localDate = new Date(year, month - 1, day); // month é 0-indexed
      return format(localDate, 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  // Filtrar escalações baseado nos critérios selecionados
  const filteredLineups = useMemo(() => {
    let filtered = lineups;

    // Filtro por tipo de data
    if (filterType === 'month') {
      filtered = filtered.filter(lineup => {
        const lineupMonth = lineup.data_escalacao.slice(0, 7); // YYYY-MM
        return lineupMonth === selectedMonth;
      });
    } else if (filterType === 'specific') {
      filtered = filtered.filter(lineup => {
        const lineupDate = lineup.data_escalacao.split(' ')[0]; // YYYY-MM-DD
        return lineupDate === selectedDate;
      });
    }

    // Filtro por busca de texto
    if (searchTerm) {
      filtered = filtered.filter(lineup => 
        lineup.formacao_nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lineup.tipo_formacao.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [lineups, filterType, selectedMonth, selectedDate, searchTerm]);

  if (loading) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold">Escalações Salvas</h2>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Trophy className="w-4 h-4" />
          <span>{filteredLineups.length} escalações</span>
        </div>
      </div>

      {/* Filtros */}
      <div className="space-y-4 mb-6">
        {/* Busca */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por formação..."
            className="input pl-10 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filtros de Data */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium">Filtrar por data:</span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`btn text-sm ${
                filterType === 'all' ? 'btn-primary' : 'btn-outline'
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilterType('month')}
              className={`btn text-sm ${
                filterType === 'month' ? 'btn-primary' : 'btn-outline'
              }`}
            >
              Por Mês
            </button>
            <button
              onClick={() => setFilterType('specific')}
              className={`btn text-sm ${
                filterType === 'specific' ? 'btn-primary' : 'btn-outline'
              }`}
            >
              Data Específica
            </button>
          </div>

          {/* Input de Mês */}
          {filterType === 'month' && (
            <div>
              <label htmlFor="monthFilter" className="block text-sm font-medium mb-1">
                Selecionar Mês
              </label>
              <input
                type="month"
                id="monthFilter"
                className="input w-full"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
            </div>
          )}

          {/* Input de Data Específica */}
          {filterType === 'specific' && (
            <div>
              <label htmlFor="dateFilter" className="block text-sm font-medium mb-1">
                Selecionar Data
              </label>
              <input
                type="date"
                id="dateFilter"
                className="input w-full"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Lista de Escalações em Cards */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {filteredLineups.length === 0 ? (
          <div className="text-center py-8">
            <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {searchTerm || filterType !== 'all' 
                ? 'Nenhuma escalação encontrada com os filtros aplicados'
                : 'Nenhuma escalação salva ainda'
              }
            </p>
            {(searchTerm || filterType !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                }}
                className="btn-outline text-sm mt-3"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredLineups.map(lineup => (
              <div 
                key={lineup.id} 
                className="p-4 border dark:border-dark-border rounded-lg hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-200 bg-white dark:bg-dark-paper hover:bg-gray-50 dark:hover:bg-dark-light cursor-pointer"
                onClick={() => onLoadLineup(lineup)}
              >
                {/* Header do Card */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                      <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <p className="font-bold text-lg">{formatDate(lineup.data_escalacao)}</p>
                      <p className="text-xs text-gray-500">
                        Criada em {format(new Date(lineup.criado_em), 'dd/MM/yyyy', { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                  
                  <span className={`text-xs px-3 py-1 rounded-full capitalize font-medium flex items-center gap-1 ${getFormationTypeColor(lineup.tipo_formacao)}`}>
                    <span>{getFormationTypeIcon(lineup.tipo_formacao)}</span>
                    {lineup.tipo_formacao}
                  </span>
                </div>

                {/* Formação */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4 text-yellow-500" />
                    <p className="font-semibold text-primary-600 dark:text-primary-400">
                      {lineup.formacao_nome}
                    </p>
                  </div>
                  
                  {/* Estatísticas */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
                      <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <div>
                        <p className="text-xs text-gray-500">Titulares</p>
                        <p className="font-bold text-green-600 dark:text-green-400">
                          {lineup.jogadores_titulares.length}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                      <Trophy className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <div>
                        <p className="text-xs text-gray-500">Reservas</p>
                        <p className="font-bold text-blue-600 dark:text-blue-400">
                          {lineup.jogadores_reservas.length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex gap-2 pt-3 border-t dark:border-dark-border">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onLoadLineup(lineup);
                    }}
                    className="btn-primary text-sm flex-1 flex items-center justify-center gap-1"
                  >
                    <Trophy className="w-4 h-4" />
                    Carregar
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicateLineup(lineup);
                    }}
                    className="btn-outline text-sm p-2 flex items-center justify-center"
                    title="Duplicar escalação"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteLineup(lineup.id);
                    }}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-lg transition-colors flex items-center justify-center"
                    title="Excluir escalação"
                    disabled={deleting === lineup.id}
                  >
                    {deleting === lineup.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Indicador de hover */}
                <div className="mt-2 text-center">
                  <p className="text-xs text-gray-400 group-hover:text-primary-500 transition-colors">
                    Clique para carregar escalação
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedLineups;